import { NotificationFilter } from "./types";
import { isNotificationUnread } from "../NotificationCard/content";

import type { NotificationFilterOption } from "./types";

export const NOTIFICATION_FILTER_STORAGE_KEY = "notification_filter";

export const NOTIFICATION_FILTER_OPTIONS: Record<
  NotificationFilter,
  NotificationFilterOption
> = {
  [NotificationFilter.ALL]: {
    label: "All",
    emptyMessage: "You do not currently have any notifications.",
  },
  [NotificationFilter.UNREAD]: {
    label: "Unread",
    emptyMessage: "You have no unread notifications.",
  },
  [NotificationFilter.READ]: {
    label: "Read",
    emptyMessage: "You have no read notifications.",
  },
};

function isNotificationFilter(
  value: string | null,
): value is NotificationFilter {
  return (
    value !== null && Object.values<string>(NotificationFilter).includes(value)
  );
}

export function filterNotifications(props: {
  notifications: Notification[];
  filter: NotificationFilter;
}): Notification[] {
  const { notifications, filter } = props;
  switch (filter) {
    case NotificationFilter.UNREAD:
      return notifications.filter(isNotificationUnread);
    case NotificationFilter.READ:
      return notifications.filter(
        (notification) => !isNotificationUnread(notification),
      );
    default:
      return notifications;
  }
}

// sessionStorage can throw in private windows or with blocked site data, so
// the modal falls back to All instead of failing.
export function getStoredNotificationFilter(): NotificationFilter {
  try {
    const stored = window.sessionStorage.getItem(
      NOTIFICATION_FILTER_STORAGE_KEY,
    );
    return isNotificationFilter(stored) ? stored : NotificationFilter.ALL;
  } catch {
    return NotificationFilter.ALL;
  }
}

export function storeNotificationFilter(props: {
  filter: NotificationFilter;
}): void {
  try {
    window.sessionStorage.setItem(
      NOTIFICATION_FILTER_STORAGE_KEY,
      props.filter,
    );
  } catch {
    // Storage is a convenience only; the filter still works for this session.
  }
}

// Expects notifications that can render, so a tab's count matches its cards.
export function countNotificationsByFilter(props: {
  notifications: Notification[];
}): Record<NotificationFilter, number> {
  const unreadCount = props.notifications.filter(isNotificationUnread).length;
  return {
    [NotificationFilter.ALL]: props.notifications.length,
    [NotificationFilter.UNREAD]: unreadCount,
    [NotificationFilter.READ]: props.notifications.length - unreadCount,
  };
}
